import type { ColDef, ColGroupDef, GridApi, RowNode } from 'ag-grid-community'
import type { ViewHook } from 'phoenix_live_view'
import { isObjectType } from 'remeda'
import type {
  GridColumn,
  ProcessColumnsFunction
} from '../utils/column-processors'
import { normalizeGridColumns } from '../utils/grid-config'
import { type GridRuntime, normalizeGridRowId } from '../utils/runtime'
import {
  type DatasourceWithCache,
  seedServerSideFirstPage
} from '../utils/server-side-datasource'
import type {
  ActiveEditsMap,
  CellValueChangePayload,
  ServerReply
} from './cell-value-change'
import type { ColumnData, ColumnUpdatePayload } from './column-updates'
import {
  applyColumnStateChanges,
  applyStructuralChanges,
  applyVisibilityChanges,
  detectColumnChanges,
  shouldUpdatePinnedData,
  updatePinnedBottomRowData
} from './column-updates'
import type { GridSorting } from './sorting'

/**
 * Beautiful event handlers for AG Grid
 * Type-safe functions for processing grid events, SSRM pagination, and Phoenix LiveView integration
 */

// Phoenix event payloads
type RefetchDataPayload = {
  readonly id: string
}

type SetColumnsPayload = {
  readonly sorting?: unknown
  readonly id: string
  readonly columns: unknown
}

type SetDataPayload = {
  readonly sorting?: unknown
  readonly id: string
  readonly data?: readonly unknown[]
  readonly columns?: unknown
  readonly forceRefresh?: boolean
  readonly meta?: {
    readonly total_count: number
    readonly next_page: number | null
  }
}

type UpdateDataPayload = {
  readonly id: string
  readonly data?: readonly Record<string, unknown>[]
  readonly forceRefresh?: boolean
  readonly bottom?: boolean
}

type SetReadOnlyPayload = {
  readonly id: string
  readonly readOnly: boolean
}

type GridRuntimeContext = {
  readonly readonly?: boolean
}

type SetSelectionPayload = {
  readonly id: string
  readonly selectedRows?: readonly string[]
}

type DeleteRowsPayload = {
  readonly id: string
  readonly rows: readonly string[]
}

type GridColumnDefinition = ColDef | ColGroupDef

type FocusedCell = ReturnType<GridApi['getFocusedCell']>
type EditingCells = ReturnType<GridApi['getEditingCells']>

/**
 * Decide whether a realtime row update may restore the grid's focused cell.
 *
 * AG Grid keeps a *logical* focused cell - `getFocusedCell()` stays non-null -
 * long after DOM focus has left the grid, so we must not blindly re-focus it.
 * Two cases to separate, using whichever element currently owns DOM focus:
 *
 *  - Focus was deliberately taken by another concrete element (e.g. an input in
 *    the document-item properties panel). LiveView preserves focus on inputs it
 *    manages across re-renders, so `activeElement` is that element. Restoring
 *    would steal focus mid-typing -> DON'T restore.
 *
 *  - Focus bounced to <body> (or nothing owns it). A surrounding LiveView
 *    re-render - e.g. re-streaming an offer section after a qty/price edit -
 *    drops focus from the AG Grid cell, because the grid lives in a
 *    `phx-update="ignore"` subtree LiveView will not refocus. Here we DO want to
 *    restore the cell so the user keeps their place in the grid.
 *
 * So: restore when focus is inside the grid, or when no concrete element holds
 * it; skip only when some other element on the page genuinely owns focus.
 */
export const shouldRestoreGridFocus = (
  root: Element | null,
  active: Element | null
): boolean =>
  !active ||
  active === active.ownerDocument.body ||
  Boolean(root?.contains(active))

/**
 * Restore the focused cell + any open editor after a row data update.
 *
 * AG Grid drops focusedCell and editingCells whenever rowData is replaced or a
 * server-side transaction lands, so re-applying both keeps keyboard navigation
 * working and stops an open editor collapsing mid-edit when the server pushes a
 * refresh for the row the user is on. `canRestore` (see shouldRestoreGridFocus)
 * gates it so a refresh never pulls focus into the grid while the user is typing
 * elsewhere.
 */
const restoreGridFocus = (
  grid: GridApi,
  focusedCell: FocusedCell,
  editingCells: EditingCells,
  canRestore: boolean
): void => {
  if (!(canRestore && focusedCell)) {
    return
  }

  grid.setFocusedCell(focusedCell.rowIndex, focusedCell.column)

  if (editingCells.length > 0) {
    grid.startEditingCell({
      rowIndex: focusedCell.rowIndex,
      colKey: focusedCell.column
    })
  }
}

const createReadonlyContext = (
  context: unknown,
  readonly: boolean
): GridRuntimeContext =>
  isObjectType(context) ? { ...context, readonly } : { readonly }

const hasCustomEditableFunction = (column: GridColumnDefinition): boolean =>
  'children' in column
    ? column.children.some((child) =>
        hasCustomEditableFunction(child as GridColumnDefinition)
      )
    : typeof column.editable === 'function'

/** Configure grid readonly options consistently with comprehensive state management */
export const configureGridReadonly = (
  grid: GridApi,
  readonly: boolean
): void => {
  grid.setGridOption(
    'context',
    createReadonlyContext(grid.getGridOption('context'), readonly)
  )

  // Set core readonly options
  grid.setGridOption('suppressClickEdit', readonly)
  grid.setGridOption('singleClickEdit', false)

  // IMPORTANT: Never suppress clipboard paste globally
  // Allow clipboard operations - column-level editable functions control which cells can be pasted into
  grid.setGridOption('suppressClipboardPaste', false)

  // Don't override column-level editable functions with defaultColDef
  // Column processors already handle editable logic based on column.editor
  // Setting defaultColDef.editable would override column-level functions
  // Instead, rely on column editable callbacks to prevent manual editing in readonly mode

  // Hide/show special columns based on readonly state
  const allColumns = grid.getAllGridColumns()
  const columnsToToggle: string[] = []

  // Find checkbox selection column (auto-generated by AG Grid)
  const selectionColumn = allColumns.find((col) => {
    const colId = col.getColId()
    return (
      colId === 'ag-Grid-AutoColumn' ||
      colId.startsWith('ag-Grid-SelectionColumn')
    )
  })
  if (selectionColumn) {
    columnsToToggle.push(selectionColumn.getColId())
  }

  // Find drag handle column (custom column)
  const dragHandleColumn = allColumns.find((col) => {
    const colDef = col.getColDef()
    return colDef.field === '__dragHandle'
  })

  if (dragHandleColumn) {
    const dragHandleColId = dragHandleColumn.getColId()
    columnsToToggle.push(dragHandleColId)

    // Update cellRendererParams to reflect new readonly state
    // This is critical for the RowDraggerComponent to properly register/unregister drag handlers
    const colDef = dragHandleColumn.getColDef()
    colDef.cellRendererParams = { readonly }

    // Update cell class to reflect drag state
    colDef.cellClass = readonly
      ? 'ag-row-drag-cell ag-row-drag-disabled'
      : 'ag-row-drag-cell'

    // Refresh drag handle cells to re-render with updated params
    // This ensures registerRowDragger is called with the correct readonly state
    grid.refreshCells({ columns: [dragHandleColId], force: true })
  }

  // Toggle visibility: hide in readonly mode, show in edit mode
  if (columnsToToggle.length > 0) {
    grid.setColumnsVisible(columnsToToggle, !readonly)
  }

  // Only refresh columns if they have custom editable functions that need re-evaluation
  const columnDefs =
    (grid.getGridOption('columnDefs') as GridColumnDefinition[]) ?? []
  const hasCustomEditableFunctions = columnDefs.some(hasCustomEditableFunction)

  if (hasCustomEditableFunctions) {
    // Use refreshCells instead of resetting columnDefs to avoid triggering editor events
    grid.refreshCells({ force: true })
  }
}

/**
 * Grid Model Type Helpers - Pure functions for grid data refresh operations
 */

type GridModelType = 'serverSide' | 'infinite' | 'clientSide'

/** Get grid model type with safe fallback */
const getGridModelType = (grid: GridApi): GridModelType => {
  const modelType = grid.getGridOption('rowModelType')
  return (modelType as GridModelType) || 'clientSide'
}

/** Upsert rows (update existing, add new) using server-side transaction */
const upsertServerSideRows = (
  grid: GridApi,
  data: readonly Record<string, unknown>[],
  runtime: GridRuntime,
  bottom: boolean
): void => {
  withBackendUpdate(data, runtime, () => {
    // Collect existing row IDs for efficient lookup
    const existingIds = new Set<string>()
    grid.forEachNode((node) => {
      if (node.data && typeof node.data === 'object' && 'id' in node.data) {
        const id = normalizeGridRowId(node.data.id)

        if (id !== undefined) {
          existingIds.add(id)
        }
      }
    })

    // Separate data into updates (existing rows) and adds (new rows)
    const updates: Record<string, unknown>[] = []
    const adds: Record<string, unknown>[] = []

    for (const row of data) {
      if (row && 'id' in row) {
        const id = normalizeGridRowId(row.id)

        if (id === undefined) {
          continue
        }

        if (existingIds.has(id)) {
          updates.push(row)
        } else {
          adds.push(row)
        }
      }
    }

    // New rows go to the top unless the caller asks to append them
    grid.applyServerSideTransaction({
      update: updates,
      add: adds,
      ...(bottom ? {} : { addIndex: 0 })
    })
  })
}

// Meta shape matching GridReply['meta'] from server-side-datasource.ts
type DatasourceMeta = {
  readonly current_page: number
  readonly next_page: number | null
  readonly page_size: number
  readonly total_count: number
  readonly total_pages: number
}

// Optional cache resetter injected from the hook
type ResetCacheFn = (
  data: readonly Record<string, unknown>[],
  meta?: DatasourceMeta
) => void

/** Refresh server-side grid with cache+purge to eliminate stale blocks.
 *
 * When `resetCache` is available (SSRM with datasource):
 *   1. Calls `refreshServerSide({ purge: true })` — clears ALL blocks
 *   2. Releases outgoing loads and caches page-1 data in the same turn
 *   3. AG Grid's queued loader calls `datasource.getRows` for block 0
 *   4. The datasource returns cached data synchronously — no loading flash
 *   5. Other blocks are fetched on-demand when the user scrolls
 *
 * Fallback (no resetCache): uses `applyServerSideRowData` directly.
 */
const refreshServerSideGrid = (
  grid: GridApi,
  runtime: GridRuntime,
  purge: boolean,
  forceRefresh?: boolean,
  data?: readonly Record<string, unknown>[],
  totalCount?: number,
  resetCache?: ResetCacheFn
): void => {
  // Backend sent fresh data (after filter/sort/page change)
  if (data && Array.isArray(data)) {
    if (data.length > 0) {
      if (resetCache) {
        // Replace the native cache before releasing its outstanding load slots.
        // Seed page one before AG Grid's queued loader requests the new block.
        const meta: DatasourceMeta | undefined =
          totalCount == null
            ? undefined
            : {
                current_page: 1,
                next_page: data.length < totalCount ? 2 : null,
                page_size: data.length,
                total_count: totalCount,
                total_pages: Math.ceil(
                  totalCount / (data.length || totalCount || 1)
                )
              }
        grid.refreshServerSide({ purge: true })
        resetCache(data, meta)
      } else {
        // Fallback: direct apply (for grids without datasource cache)
        withBackendUpdate(data, runtime, () => {
          grid.applyServerSideRowData({
            successParams: {
              rowData: data as unknown[],
              rowCount: totalCount
            },
            startRow: 0
          })
        })
      }
    } else if (resetCache) {
      // Handle empty array - clear the grid via cache+purge
      grid.refreshServerSide({ purge: true })
      resetCache([], undefined)
    } else {
      // Handle empty array - clear the grid via direct apply
      grid.applyServerSideRowData({
        successParams: { rowData: [], rowCount: 0 },
        startRow: 0
      })
    }
    return
  }

  // No data means just purge/refresh as before
  if (forceRefresh === true) {
    grid.refreshServerSide({ purge })
  }
}

/** Refresh server-side grid with upsert (update or add rows) */
const refreshServerSideGridUpsert = (
  grid: GridApi,
  runtime: GridRuntime,
  data?: readonly Record<string, unknown>[],
  bottom = false
): void => {
  // Upsert specific rows (update existing, add new)
  if (data && data.length > 0) {
    upsertServerSideRows(grid, data, runtime, bottom)
  } else if (data && data.length === 0) {
    // biome-ignore lint/suspicious/noConsole: Debug logging for empty data handling
    console.log(
      'AG Grid: Update-or-add-data received empty array (no rows to upsert)'
    )
  }
}

/** Refresh infinite model grid */
const refreshInfiniteGrid = (grid: GridApi): void => {
  grid.purgeInfiniteCache()
}

const withBackendUpdate = <Result>(
  data: readonly unknown[],
  runtime: GridRuntime,
  update: () => Result
): Result => {
  const finish = runtime.beginBackendUpdate(data)

  try {
    return update()
  } finally {
    finish()
  }
}

/** Refresh or update client-side grid */
const refreshClientSideGrid = (
  grid: GridApi,
  runtime: GridRuntime,
  data?: readonly unknown[]
): void => {
  if (data && Array.isArray(data)) {
    // No normalization needed - stepper component handles it during render
    withBackendUpdate(data, runtime, () => {
      grid.setGridOption('rowData', data)
    })

    // Log when clearing grid with empty data
    if (data.length === 0) {
      // biome-ignore lint/suspicious/noConsole: Debug logging for empty data handling
      console.log('AG Grid: Clearing client-side grid with empty data array')
    }
  } else {
    grid.refreshClientSideRowModel()
  }
}

/** Dispatch grid refresh based on model type */
const refreshGridByModelType = (
  grid: GridApi,
  modelType: GridModelType,
  options: {
    readonly purge?: boolean
    readonly forceRefresh?: boolean
    readonly data?: readonly unknown[]
    readonly totalCount?: number
    readonly resetCache?: ResetCacheFn
  },
  runtime: GridRuntime
): void => {
  if (modelType === 'serverSide') {
    const typedData = options.data as
      | readonly Record<string, unknown>[]
      | undefined
    refreshServerSideGrid(
      grid,
      runtime,
      options.purge ?? true,
      options.forceRefresh,
      typedData,
      options.totalCount,
      options.resetCache
    )
  } else if (modelType === 'infinite') {
    refreshInfiniteGrid(grid)
  } else {
    refreshClientSideGrid(grid, runtime, options.data)
  }
}

/** Clear backend-only sorting blocks without painting a one-row loading store. */
const refreshSortingStore = (grid: GridApi): void => {
  const count = grid.getDisplayedRowCount()
  const lastRowKnown = grid.isLastRowIndexKnown()

  grid.refreshServerSide({ purge: true })
  grid.setRowCount(count, lastRowKnown)
}

/** Keep column-only updates from replacing unchanged definitions. */
const applyServerColumns = (
  grid: GridApi,
  columns: readonly GridColumn[],
  processColumns: ProcessColumnsFunction,
  context?: { readonly movableRows: boolean; readonly readonly: boolean }
): void => {
  const currentColumns = grid.getColumnDefs() ?? []
  const change = detectColumnChanges(currentColumns, columns)

  if (change.type === 'visibility-only') {
    applyVisibilityChanges(grid, change.changes)
  } else if (change.type === 'state-only') {
    applyColumnStateChanges(grid, columns)
  } else {
    applyStructuralChanges(
      grid,
      currentColumns,
      columns,
      processColumns,
      context
    )
  }

  if (shouldUpdatePinnedData(columns)) {
    updatePinnedBottomRowData(grid, columns)
  }
}

/** Create Phoenix event handlers for grid updates with comprehensive error handling */
export const createPhoenixEventHandlers = (
  hook: Pick<ViewHook, 'el'> & {
    grid: GridApi
    _gridContext?: { readonly movableRows: boolean; readonly readonly: boolean }
    _gridSorting?: GridSorting
    _resetDatasourceCache?: ResetCacheFn
    _invalidateDatasourceCache?: () => void
    _retireDatasourceCache?: () => void
    _resumeDatasourceRequests?: DatasourceWithCache['resumeRequests']
  },
  processColumns: ProcessColumnsFunction,
  runtime: GridRuntime
) => ({
  'grid:set-columns': (payload: SetColumnsPayload): void => {
    if (payload.id !== hook.el.id) {
      return
    }

    // Guard against race condition: grid may not be initialized yet
    if (!hook.grid) {
      return
    }

    try {
      const columns = normalizeGridColumns(payload.columns)

      if (!columns) {
        console.error(
          'Ignored invalid column definitions for grid:',
          payload.id
        )
        return
      }

      applyServerColumns(hook.grid, columns, processColumns, hook._gridContext)

      const sortingChanged = hook._gridSorting?.accept(
        hook.grid,
        payload.sorting
      )
      if (
        sortingChanged &&
        sortingChanged !== 'unchanged' &&
        hook.grid.getGridOption('rowModelType') === 'serverSide'
      ) {
        if (sortingChanged === 'native-reset') {
          hook._invalidateDatasourceCache?.()
          hook._resumeDatasourceRequests?.()
        } else {
          refreshSortingStore(hook.grid)
          hook._retireDatasourceCache?.()
        }
      }
    } catch (error) {
      console.error(
        'Failed to update column definitions for grid:',
        payload.id,
        error
      )
    }
  },

  // Keep loaded rows visible while the datasource refreshes every cached block.
  'grid:refetch-data': (payload: RefetchDataPayload): void => {
    const grid = hook.grid

    if (
      payload.id !== hook.el.id ||
      !grid ||
      grid.isDestroyed() ||
      getGridModelType(grid) !== 'serverSide'
    ) {
      return
    }

    hook._invalidateDatasourceCache?.()
    grid.refreshServerSide({ purge: false })
  },

  'grid:set-data': (payload: SetDataPayload): void => {
    if (payload.id !== hook.el.id) {
      return
    }

    // Guard against race condition: grid may not be initialized yet
    if (!hook.grid) {
      return
    }

    try {
      const columns =
        payload.columns === undefined
          ? undefined
          : normalizeGridColumns(payload.columns)

      if (payload.columns !== undefined && !columns) {
        console.error(
          'Ignored invalid column definitions for grid:',
          payload.id
        )
        return
      }

      if (columns) {
        applyStructuralChanges(
          hook.grid,
          hook.grid.getColumnDefs() ?? [],
          columns,
          processColumns,
          hook._gridContext
        )
        if (shouldUpdatePinnedData(columns)) {
          updatePinnedBottomRowData(hook.grid, columns)
        }
      }

      const sorting = hook._gridSorting?.accept(hook.grid, payload.sorting)
      const data = payload.data
      const modelType = getGridModelType(hook.grid)
      if (modelType === 'serverSide' && sorting && data) {
        hook._invalidateDatasourceCache?.()
        // Native sorting already cleared its blocks. Other accepted data
        // resets still need to discard stale pages, preserving loading size.
        if (sorting !== 'native-reset') {
          refreshSortingStore(hook.grid)
          hook._retireDatasourceCache?.()
        }
        withBackendUpdate(data, runtime, () =>
          seedServerSideFirstPage(hook.grid, data, {
            total_count: payload.meta?.total_count ?? data.length
          })
        )
        hook._resumeDatasourceRequests?.({
          data,
          totalCount: payload.meta?.total_count ?? data.length
        })
        return
      }

      refreshGridByModelType(
        hook.grid,
        modelType,
        {
          purge: true,
          forceRefresh: payload.forceRefresh,
          data: payload.data,
          totalCount: payload.meta?.total_count,
          resetCache: hook._resetDatasourceCache
        },
        runtime
      )
    } catch (error) {
      console.error('Failed to refresh grid data for grid:', payload.id, error)
    }
  },

  'grid:update-data': (payload: UpdateDataPayload): void => {
    if (payload.id !== hook.el.id) {
      return
    }

    const grid = hook.grid

    // Updates and deferred restores belong to this grid instance only.
    if (!grid || grid.isDestroyed()) {
      return
    }

    const wasAnimating = grid.getGridOption('animateRows')

    try {
      // Temporarily disable animations to prevent visual reshuffling
      if (wasAnimating) {
        grid.setGridOption('animateRows', false)
      }

      // Capture focus + edit state BEFORE the transaction so keyboard nav and
      // open editors survive (applyServerSideTransaction/setRowData drop both).
      // getFocusedCell() stays set even after focus leaves the grid, so also
      // decide whether restoring is allowed: re-focus the grid cell when focus
      // is inside the grid or bounced to <body> by a surrounding re-render, but
      // never when another element (e.g. the properties panel input) owns focus.
      const focusedCell = grid.getFocusedCell()
      const editingCells = grid.getEditingCells()
      const canRestoreFocus = shouldRestoreGridFocus(
        hook.el,
        document.activeElement
      )

      const modelType = getGridModelType(grid)

      // Use transaction updates instead of full refresh to minimize disruption
      if (modelType === 'serverSide') {
        const typedData = payload.data
        if (typedData && typedData.length > 0) {
          // Use transaction update for SSRM (no animation)
          withBackendUpdate(typedData, runtime, () => {
            const transaction = grid.applyServerSideTransaction({
              update: [...typedData]
            })

            if (transaction?.update && transaction.update.length > 0) {
              grid.refreshCells({
                force: true,
                rowNodes: [...transaction.update]
              })
            }
          })
        } else if (typedData && typedData.length === 0) {
          // biome-ignore lint/suspicious/noConsole: Debug logging for empty data handling
          console.log(
            'AG Grid: Update-data received empty array (no rows to update)'
          )
        }
      } else {
        // For client-side/infinite models
        refreshGridByModelType(
          grid,
          modelType,
          {
            purge: false,
            forceRefresh: payload.forceRefresh,
            data: payload.data
          },
          runtime
        )
      }

      restoreGridFocus(grid, focusedCell, editingCells, canRestoreFocus)

      // Teardown cancels the restore; already-disabled grids need no frame.
      if (wasAnimating) {
        runtime.scheduleFrame(() => {
          if (!grid.isDestroyed()) {
            grid.setGridOption('animateRows', wasAnimating)
          }
        })
      }
    } catch (error) {
      console.error('Failed to update grid data for grid:', payload.id, error)

      if (wasAnimating && !grid.isDestroyed()) {
        grid.setGridOption('animateRows', wasAnimating)
      }
    }
  },

  'grid:update-or-add-data': (payload: UpdateDataPayload): void => {
    if (payload.id !== hook.el.id) {
      return
    }

    // Guard against race condition: grid may not be initialized yet
    if (!hook.grid) {
      return
    }

    try {
      const modelType = getGridModelType(hook.grid)

      // For server-side model, use upsert logic
      if (modelType === 'serverSide') {
        const typedData = payload.data as
          | readonly Record<string, unknown>[]
          | undefined
        refreshServerSideGridUpsert(
          hook.grid,
          runtime,
          typedData,
          payload.bottom === true
        )
      } else {
        // For client-side/infinite, fall back to regular update
        refreshGridByModelType(
          hook.grid,
          modelType,
          {
            purge: false,
            forceRefresh: payload.forceRefresh,
            data: payload.data
          },
          runtime
        )
      }
    } catch (error) {
      console.error('Failed to upsert grid data for grid:', payload.id, error)
    }
  },

  'grid:set-read-only': (payload: SetReadOnlyPayload): void => {
    if (payload.id === hook.el.id) {
      // Guard against race condition: grid may not be initialized yet
      if (!hook.grid) {
        return
      }

      try {
        const readonly = Boolean(payload.readOnly)
        hook._gridContext = {
          movableRows: hook._gridContext?.movableRows ?? false,
          readonly
        }
        configureGridReadonly(hook.grid, readonly)
      } catch (error) {
        // Log error but don't break the grid
        console.error('Failed to configure grid readonly state:', error)
      }
    }
  },

  'grid:set-selection': (payload: SetSelectionPayload): void => {
    if (payload.id === hook.el.id) {
      // Guard against race condition: grid may not be initialized yet
      if (!hook.grid) {
        return
      }

      try {
        // Clear current selection and set new selection
        hook.grid.deselectAll()
        if (payload.selectedRows && payload.selectedRows.length > 0) {
          hook.grid.forEachNode((node: RowNode) => {
            if (payload.selectedRows?.includes(String(node.data?.id))) {
              node.setSelected(true)
            }
          })
        }
      } catch (error) {
        console.error('Failed to set grid selection:', error)
      }
    }
  },

  'grid:delete-rows': (payload: DeleteRowsPayload): void => {
    if (payload.id !== hook.el.id) {
      return
    }

    // Guard against race condition: grid may not be initialized yet
    if (!hook.grid) {
      return
    }

    try {
      const modelType = getGridModelType(hook.grid)
      const rowsToDelete: unknown[] = []

      // Collect row data objects for the IDs we want to delete
      hook.grid.forEachNode((node: RowNode) => {
        if (
          node.data &&
          'id' in node.data &&
          payload.rows.includes(String(node.data.id))
        ) {
          rowsToDelete.push(node.data)
        }
      })

      if (rowsToDelete.length === 0) {
        console.warn('No rows found to delete for IDs:', payload.rows)
        return
      }

      // Apply appropriate transaction based on model type
      if (modelType === 'serverSide') {
        hook.grid.applyServerSideTransaction({ remove: rowsToDelete })
      } else if (modelType === 'clientSide') {
        hook.grid.applyTransaction({ remove: rowsToDelete })
      } else {
        console.warn('Delete rows not supported for infinite row model')
      }
    } catch (error) {
      console.error('Failed to delete rows from grid:', payload.id, error)
    }
  }
})

// Export types for external use
export type {
  ActiveEditsMap,
  CellValueChangePayload,
  ColumnData,
  ColumnUpdatePayload,
  DeleteRowsPayload,
  ServerReply,
  SetColumnsPayload,
  SetDataPayload,
  SetReadOnlyPayload,
  SetSelectionPayload,
  UpdateDataPayload
}
